import ky, { type KyInstance } from 'ky';

import { env } from '@/core/config/env';
import { getSelectedBranchHeader } from '@/features/tenants';

import { clearTokens, readTokens, writeTokens } from './token-store';

/**
 * Cliente HTTP global (ky com hooks).
 *
 * Responsabilidades:
 *   - prefixUrl: base da API (default `/api/v1`, proxy do Vite em dev)
 *   - beforeRequest: anexa `Authorization: Bearer <token>` quando há
 *   - afterResponse: retry-once em 401 chamando /auth/refresh
 *   - timeout sane (30s) — pra não pendurar a UI em servidor lento
 *
 * O retry de 401 acontece UMA vez. Se o refresh falhar, limpa tokens —
 * a próxima chamada protegida cai pro guard de rota que redireciona /login.
 * Mantemos store separado (`token-store`) pra encapsular localStorage.
 */

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Tenta renovar o access token com o refresh atual. Retorna true em sucesso,
 * false em falha (e nesse caso já limpou os tokens).
 *
 * Concorrência: se 3 requests dispararem 401 ao mesmo tempo, todas esperam
 * o MESMO refresh em vôo — evita disparar 3 chamadas de /auth/refresh.
 */
async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const tokens = readTokens();
    if (!tokens) return false;

    try {
      const response = await fetch(`${env.VITE_API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: tokens.refreshToken }),
      });
      if (!response.ok) {
        clearTokens();
        return false;
      }
      const refreshed = (await response.json()) as {
        accessToken: string;
        refreshToken: string;
        accessTokenExpiresAt: string;
        refreshTokenExpiresAt: string;
      };
      writeTokens(refreshed);
      return true;
    } catch {
      clearTokens();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export const http: KyInstance = ky.create({
  // ky 2.x renomeou prefixUrl -> prefix
  prefix: env.VITE_API_BASE_URL,
  timeout: 30_000,
  // Retries do ky ficam off — gerenciamos retry-on-401 manualmente.
  retry: 0,
  hooks: {
    // ky 2.x: hooks recebem um objeto state em vez de args posicionais.
    beforeRequest: [
      (state) => {
        const tokens = readTokens();
        if (tokens?.accessToken) {
          state.request.headers.set('Authorization', `Bearer ${tokens.accessToken}`);
        }
        // X-Protheus-Tenant override por chamada (formato "empresa,filial").
        // Backend prioriza esse header sobre o tenantHeader salvo no banco.
        // Lemos do Zustand store via helper sem hook — evita acoplar HTTP
        // layer a React.
        const branchHeader = getSelectedBranchHeader();
        if (branchHeader) {
          state.request.headers.set('X-Protheus-Tenant', branchHeader);
        }
      },
    ],
    afterResponse: [
      async (state) => {
        // Endpoints de auth não disparam refresh (evita loop infinito).
        const url = state.request.url;
        if (url.includes('/auth/login') || url.includes('/auth/refresh')) {
          return state.response;
        }
        if (state.response.status !== 401) return state.response;

        // 401 detectado — tenta refresh + repete a request original 1×.
        const ok = await refreshAccessToken();
        if (!ok) return state.response;

        const newTokens = readTokens();
        if (!newTokens) return state.response;

        const retried = state.request.clone();
        retried.headers.set('Authorization', `Bearer ${newTokens.accessToken}`);
        return fetch(retried);
      },
    ],
  },
});
